import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeftIcon, ChevronRightIcon, XSmall20Icon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { slideIndexAt } from "./product-gallery";

/** Сколько пикселей протянуть вниз, чтобы закрыть просмотр свайпом. */
export const SWIPE_CLOSE_PX = 110;
const HIDE_SCROLLBAR = "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

/** Жест вниз закрывает просмотр, если он в основном вертикальный и длинный. */
export const isSwipeDownClose = (dx: number, dy: number): boolean => dy > SWIPE_CLOSE_PX && Math.abs(dy) > Math.abs(dx) * 1.2;

// Лайтбокс увеличения фото (кадры «Увеличение фото» 3853:155897 / 3853:156231):
// сплошной белый фон, фото по центру (r8), кнопки — кружки #C2C4CD 18 % без
// тени: ‹/› 48px в 30px от самого фото (у вертикального фото — рядом с ним), но
// не ближе 30px к краю экрана (у широкого фото); × 40px (20px сверху, 30px
// справа). Счётчика «1 / 2» в кадрах нет — он только по `counter`.
// Листается свайпом (лента с прилипанием), стрелками и клавишами ←/→;
// закрывается крестиком, Esc, кликом мимо фото и свайпом вниз.
// Управляемый: индекс снаружи, общий с галереей карточки.
export function PhotoZoom(t0) {
  const $ = _c(80);
  const {
    images,
    index,
    open,
    onClose,
    onIndex,
    counter: t1
  } = t0;
  const counter = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const count = images.length;
  const current = Math.min(Math.max(index, 0), Math.max(count - 1, 0));
  let t2;
  if ($[0] !== count || $[1] !== current || $[2] !== onIndex) {
    t2 = delta => onIndex((current + delta + count) % count);
    $[0] = count;
    $[1] = current;
    $[2] = onIndex;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const go = t2;
  const track = useRef(null);
  const settle = useRef(undefined);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => clearTimeout(settle.current);
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const shown = useRef(current);
  const touch = useRef(null);
  const [pull, setPull] = useState(0);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const imgs = useRef(t5);
  const [arrows, setArrows] = useState(null);
  let t6;
  if ($[7] !== current) {
    t6 = () => {
      const img = imgs.current[current];
      const slide = img?.parentElement;
      if (!img || !slide || !img.naturalWidth) {
        return;
      }
      const r = img.getBoundingClientRect();
      const box = slide.getBoundingClientRect();
      setArrows({
        left: Math.max(30, r.left - box.left - 30 - 48),
        right: Math.max(30, box.right - r.right - 30 - 48)
      });
    };
    $[7] = current;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const measure = t6;
  let t7;
  if ($[9] !== measure || $[10] !== open) {
    t7 = () => {
      if (!open) {
        return;
      }
      measure();
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    };
    $[9] = measure;
    $[10] = open;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== count || $[13] !== current || $[14] !== open) {
    t8 = [open, current, count];
    $[12] = count;
    $[13] = current;
    $[14] = open;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[16] !== go || $[17] !== onClose || $[18] !== open) {
    t9 = () => {
      if (!open) {
        return;
      }
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        } else {
          if (e.key === "ArrowLeft") {
            go(-1);
          } else {
            if (e.key === "ArrowRight") {
              go(1);
            }
          }
        }
      };
      window.addEventListener("keydown", onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = prev;
      };
    };
    $[16] = go;
    $[17] = onClose;
    $[18] = open;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== count || $[21] !== current || $[22] !== open) {
    t10 = [open, current, count];
    $[20] = count;
    $[21] = current;
    $[22] = open;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  if ($[24] !== current || $[25] !== open) {
    t11 = () => {
      if (!open) {
        return;
      }
      const el = track.current;
      if (!el) {
        return;
      }
      shown.current = current;
      el.scrollTo({
        left: current * el.clientWidth
      });
      setPull(0);
    };
    $[24] = current;
    $[25] = open;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== open) {
    t12 = [open];
    $[27] = open;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  useLayoutEffect(t11, t12);
  let t13;
  let t14;
  if ($[29] !== current || $[30] !== open) {
    t13 = () => {
      const el_0 = track.current;
      if (!open || !el_0 || shown.current === current) {
        return;
      }
      shown.current = current;
      el_0.scrollTo({
        left: current * el_0.clientWidth,
        behavior: "smooth"
      });
    };
    t14 = [open, current];
    $[29] = current;
    $[30] = open;
    $[31] = t13;
    $[32] = t14;
  } else {
    t13 = $[31];
    t14 = $[32];
  }
  useEffect(t13, t14);
  if (!open || count === 0) {
    return null;
  }
  let t15;
  if ($[33] !== count || $[34] !== onIndex) {
    t15 = () => {
      clearTimeout(settle.current);
      settle.current = setTimeout(() => {
        const el_1 = track.current;
        if (!el_1) {
          return;
        }
        const i = slideIndexAt(el_1.scrollLeft, el_1.clientWidth, count);
        if (i !== shown.current) {
          shown.current = i;
          onIndex(i);
        }
      }, 90);
    };
    $[33] = count;
    $[34] = onIndex;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const onScroll = t15;
  let t16;
  if ($[36] !== pull) {
    t16 = pull > 0 ? {
      opacity: Math.max(0.35, 1 - pull / 400)
    } : undefined;
    $[36] = pull;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("product.photoZoom", {
      defaultValue: "\u041F\u0440\u043E\u0441\u043C\u043E\u0442\u0440 \u0444\u043E\u0442\u043E"
    });
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = e_0 => {
      const p = e_0.touches[0];
      touch.current = p && e_0.touches.length === 1 ? {
        x: p.clientX,
        y: p.clientY
      } : null;
    };
    t19 = e_1 => {
      const start = touch.current;
      const p_0 = e_1.touches[0];
      if (!start || !p_0) {
        return;
      }
      const dx = p_0.clientX - start.x;
      const dy = p_0.clientY - start.y;
      setPull(dy > 0 && Math.abs(dy) > Math.abs(dx) ? dy : 0);
    };
    $[40] = t18;
    $[41] = t19;
  } else {
    t18 = $[40];
    t19 = $[41];
  }
  let t20;
  if ($[42] !== onClose) {
    t20 = e_2 => {
      const start_0 = touch.current;
      const p_1 = e_2.changedTouches[0];
      touch.current = null;
      if (start_0 && p_1 && isSwipeDownClose(p_1.clientX - start_0.x, p_1.clientY - start_0.y)) {
        onClose();
      } else {
        setPull(0);
      }
    };
    $[42] = onClose;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t) {
    t21 = t("common.close", {
      defaultValue: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C"
    });
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <XSmall20Icon />;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== onClose || $[48] !== t21) {
    t23 = <button type="button" aria-label={t21} onClick={onClose} className="absolute right-[30px] top-5 z-10 flex size-10 items-center justify-center rounded-full bg-[#C2C4CD]/18 text-osmix-text-2 outline-none transition-colors hover:bg-[#C2C4CD]/35 focus-visible:ring-2 focus-visible:ring-ds-focus">{t22}</button>;
    $[47] = onClose;
    $[48] = t21;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== count || $[51] !== counter || $[52] !== current) {
    t24 = counter && count > 1 && <span className="absolute left-1/2 top-7 z-10 -translate-x-1/2 type-body2 text-osmix-text-2">{current + 1} / {count}</span>;
    $[50] = count;
    $[51] = counter;
    $[52] = current;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = cn("flex h-full snap-x snap-mandatory overflow-x-auto overscroll-contain", HIDE_SCROLLBAR);
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== pull) {
    t26 = pull > 0 ? {
      transform: `translateY(${pull}px)`
    } : undefined;
    $[55] = pull;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== current || $[58] !== images || $[59] !== measure || $[60] !== onClose) {
    t27 = images.map((src, i_0) => <div key={`${src}-${i_0}`} onClick={onClose} className="flex size-full shrink-0 snap-center snap-always items-center justify-center px-4 py-16 md:px-[108px] md:py-5"><img ref={el_2 => {
        imgs.current[i_0] = el_2;
      }} onLoad={i_0 === current ? measure : undefined} src={src} alt="" draggable={false} onClick={_temp} className="max-h-full max-w-full rounded-[8px] object-contain" /></div>);
    $[57] = current;
    $[58] = images;
    $[59] = measure;
    $[60] = onClose;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== onScroll || $[63] !== t26 || $[64] !== t27) {
    t28 = <div ref={track} onScroll={onScroll} data-testid="photo-zoom-track" className={t25} style={t26}>{t27}</div>;
    $[62] = onScroll;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== arrows?.left || $[67] !== arrows?.right || $[68] !== count || $[69] !== go || $[70] !== t) {
    t29 = count > 1 && <><button type="button" aria-label={t("common.prev", {
        defaultValue: "\u041D\u0430\u0437\u0430\u0434"
      })} onClick={() => go(-1)} className="absolute top-1/2 z-10 hidden size-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#C2C4CD]/18 text-osmix-text-2 outline-none transition-colors hover:bg-[#C2C4CD]/35 focus-visible:ring-2 focus-visible:ring-ds-focus [@media(hover:hover)]:flex" style={{
        left: arrows?.left ?? 30
      }}><ChevronLeftIcon /></button><button type="button" aria-label={t("common.next", {
        defaultValue: "\u0412\u043F\u0435\u0440\u0451\u0434"
      })} onClick={() => go(1)} className="absolute top-1/2 z-10 hidden size-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#C2C4CD]/18 text-osmix-text-2 outline-none transition-colors hover:bg-[#C2C4CD]/35 focus-visible:ring-2 focus-visible:ring-ds-focus [@media(hover:hover)]:flex" style={{
        right: arrows?.right ?? 30
      }}><ChevronRightIcon /></button></>;
    $[66] = arrows?.left;
    $[67] = arrows?.right;
    $[68] = count;
    $[69] = go;
    $[70] = t;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t16 || $[73] !== t17 || $[74] !== t20 || $[75] !== t23 || $[76] !== t24 || $[77] !== t28 || $[78] !== t29) {
    t30 = <div className="fixed inset-0 z-[60] bg-white transition-opacity" style={t16} role="dialog" aria-modal="true" aria-label={t17} onTouchStart={t18} onTouchMove={t19} onTouchEnd={t20}>{t23}{t24}{t28}{t29}</div>;
    $[72] = t16;
    $[73] = t17;
    $[74] = t20;
    $[75] = t23;
    $[76] = t24;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  return t30;
}
function _temp(e_3) {
  return e_3.stopPropagation();
}